/**
 * API service for cart operations
 */

import { getAuthHeaders } from './authHeaders';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

class CartAPI {
    /**
     * Get user's cart
     */
    static async getCart() {
        console.log(`🛒 CartAPI: Getting cart`);

        try {
            const headers = await getAuthHeaders();
            const response = await fetch(`${API_BASE_URL}/cart`, {
                headers
            });

            if (!response.ok) {
                if (response.status === 401) {
                    throw new Error('NOT_AUTHENTICATED');
                }
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            console.log(`✅ CartAPI: Got ${data.items?.length || 0} items`);
            return data;
        } catch (error) {
            console.error('❌ CartAPI: Error getting cart:', error);
            throw error;
        }
    }

    static async updateExtraQuantity(productId, extraId, quantity) {
        console.log(`🔄 CartAPI: Updating extra quantity product=${productId} extra=${extraId} qty=${quantity}`);

        try {
            const headers = await getAuthHeaders({
                'Content-Type': 'application/json'
            });
            const response = await fetch(`${API_BASE_URL}/cart/items/${productId}/extras/${extraId}`, {
                method: 'PUT',
                headers,
                body: JSON.stringify({ quantity })
            });

            if (!response.ok) {
                if (response.status === 401) {
                    throw new Error('NOT_AUTHENTICATED');
                }
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            console.log(`✅ CartAPI: Extra quantity updated`);
            return data;
        } catch (error) {
            console.error('❌ CartAPI: Error updating extra quantity:', error);
            throw error;
        }
    }

    /**
     * Add item to cart
     */
    static async addItem(product, quantity = 1, options = {}) {
        console.log(`➕ CartAPI: Adding ${product.name} to cart`);

        try {
            const headers = await getAuthHeaders({
                'Content-Type': 'application/json'
            });
            const response = await fetch(`${API_BASE_URL}/cart/items`, {
                method: 'POST',
                headers,
                body: JSON.stringify({
                    product_id: String(product.id),
                    quantity: quantity,
                    selected_image_url: options?.selected_image_url || null,
                    selected_extra_ids: Array.isArray(options?.selected_extra_ids) ? options.selected_extra_ids : null,
                    // SECURITY: Removed product_name, product_price, product_image
                    // Backend will fetch these from Sanity
                })
            });

            if (!response.ok) {
                if (response.status === 401) {
                    throw new Error('NOT_AUTHENTICATED');
                }
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            console.log(`✅ CartAPI: Item added successfully`);
            return data;
        } catch (error) {
            console.error('❌ CartAPI: Error adding item:', error);
            throw error;
        }
    }

    /**
     * Update item quantity
     */
    static async updateQuantity(productId, quantity) {
        console.log(`🔄 CartAPI: Updating quantity for ${productId} to ${quantity}`);

        try {
            const headers = await getAuthHeaders({
                'Content-Type': 'application/json'
            });
            const response = await fetch(`${API_BASE_URL}/cart/items/${productId}`, {
                method: 'PUT',
                headers,
                body: JSON.stringify({ quantity })
            });

            if (!response.ok) {
                if (response.status === 401) {
                    throw new Error('NOT_AUTHENTICATED');
                }
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            console.log(`✅ CartAPI: Quantity updated`);
            return data;
        } catch (error) {
            console.error('❌ CartAPI: Error updating quantity:', error);
            throw error;
        }
    }

    /**
     * Remove item from cart
     */
    static async removeItem(productId) {
        console.log(`🗑️ CartAPI: Removing ${productId} from cart`);

        try {
            const headers = await getAuthHeaders();
            const response = await fetch(`${API_BASE_URL}/cart/items/${productId}`, {
                method: 'DELETE',
                headers
            });

            if (!response.ok) {
                if (response.status === 401) {
                    throw new Error('NOT_AUTHENTICATED');
                }
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            console.log(`✅ CartAPI: Item removed`);
            return data;
        } catch (error) {
            console.error('❌ CartAPI: Error removing item:', error);
            throw error;
        }
    }

    /**
     * Clear cart
     */
    static async clearCart() {
        console.log(`🗑️ CartAPI: Clearing cart`);

        try {
            const headers = await getAuthHeaders();
            const response = await fetch(`${API_BASE_URL}/cart`, {
                method: 'DELETE',
                headers
            });

            if (!response.ok) {
                if (response.status === 401) {
                    throw new Error('NOT_AUTHENTICATED');
                }
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            console.log(`✅ CartAPI: Cart cleared`);
            return data;
        } catch (error) {
            console.error('❌ CartAPI: Error clearing cart:', error);
            throw error;
        }
    }
}

export default CartAPI;
